import { useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDistanceToNow } from 'date-fns';
import { palette, typography } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface AnalyticsHeaderProps {
  title: string;
  subtitle: string;
  category: string;
  dataUpdatedAt: number | undefined;
  isRefetching: boolean;
  onRefresh: () => void;
}

export const AnalyticsHeader = ({
  title,
  subtitle,
  category,
  dataUpdatedAt,
  isRefetching,
  onRefresh,
}: AnalyticsHeaderProps) => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const colors =
    mode === 'dark'
      ? {
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  return (
    <Box
      sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        ...animations.slideDown,
      }}
    >
      <Box>
        <Typography
          variant="overline"
          sx={{
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.widest,
            display: 'block',
            mb: 0.5,
          }}
        >
          {category}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {subtitle}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        {dataUpdatedAt && (
          <Typography
            variant="caption"
            sx={{
              color: colors.textMuted,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
            }}
          >
            Updated {formatDistanceToNow(dataUpdatedAt, { addSuffix: true })}
          </Typography>
        )}
        <Tooltip title={isRefetching ? 'Refreshing...' : 'Refresh data'}>
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={isRefetching}
              size="small"
              sx={{
                color: colors.textMuted,
                '&:hover': {
                  color: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
                '@keyframes spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: isRefetching ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default AnalyticsHeader;
